import { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import {
  registerPushNotifications,
  retryPushRegistrationIfNeeded,
  isPushTokenRegisteredLocally,
} from "../../services/pushNotifications";
import { showToast } from "../../utils/toast";
import "./PushRegistrationBanner.css";

const DISMISS_KEY = "nema_push_banner_dismissed";

function canShowBanner() {
  const role = api.getAuthRoleFromToken?.();
  return ["CUSTOMER", "CAREGIVER", "CARE_MANAGER", "ADMIN", "OPS"].includes(role);
}

/**
 * Visible reminder when the signed-in account has no browser push token on the server.
 */
export default function PushRegistrationBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusLabel, setStatusLabel] = useState("");

  const [deliveryHint, setDeliveryHint] = useState("");

  const refresh = useCallback(async () => {
    if (!api.getToken() || !canShowBanner()) {
      setVisible(false);
      return;
    }
    if (window.sessionStorage.getItem(DISMISS_KEY) === "1") {
      setVisible(false);
      return;
    }
    try {
      const [statusRes, configRes] = await Promise.all([
        api.getPushStatus(),
        api.getPushConfig().catch(() => null),
      ]);
      const permission = Notification.permission;
      const localToken = isPushTokenRegisteredLocally();
      if (!localToken) {
        window.sessionStorage.removeItem(DISMISS_KEY);
      }
      // Show until this browser has a working FCM token (server may hold a stale token from a failed attempt).
      const needsPush =
        statusRes?.webEnabled !== false &&
        permission !== "denied" &&
        !localToken;
      const dismissed = window.sessionStorage.getItem(DISMISS_KEY) === "1";
      setVisible(needsPush && !dismissed);
      if (configRes?.deliveryHint) {
        setDeliveryHint(String(configRes.deliveryHint));
      } else if (statusRes?.canDeliver === false) {
        setDeliveryHint("Server push delivery is not configured yet.");
      } else {
        setDeliveryHint("");
      }
      if (needsPush) {
        const browserName = /Edg\//.test(navigator.userAgent)
          ? "Microsoft Edge"
          : /Chrome\//.test(navigator.userAgent)
            ? "Google Chrome"
            : "this browser";
        setStatusLabel(
          statusRes?.canDeliver === false
            ? "Allow notifications here — server delivery still needs the Firebase service account file."
            : `Allow notifications in ${browserName}. Each browser (Chrome, Edge, etc.) must enable push separately.`
        );
      }
    } catch {
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = window.setTimeout(refresh, 2500);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Notification.permission === "granted" && !isPushTokenRegisteredLocally()) {
        registerPushNotifications({ force: true }).finally(refresh);
        return;
      }
      if (Notification.permission === "granted") {
        retryPushRegistrationIfNeeded().finally(refresh);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  async function enablePush() {
    if (busy) return;
    setBusy(true);
    const busyTimer = window.setTimeout(() => setBusy(false), 40000);
    try {
      const result = await Promise.race([
        registerPushNotifications({ force: true }),
        new Promise((_, reject) => {
          window.setTimeout(
            () => reject(new Error("Registration timed out — is nemacareBE running?")),
            35000
          );
        }),
      ]);
      await refresh();
      if (result?.ok) {
        showToast.success("Browser notifications enabled.");
        if (result?.serverDeliveryHint) {
          showToast.info(result.serverDeliveryHint);
        }
        setVisible(false);
      } else if (result?.reason === "denied") {
        showToast.error(
          "Notifications are blocked. Open site settings for this page and allow notifications, then click Enable again."
        );
      } else {
        showToast.info(`Could not enable push: ${result?.reason || "unknown error"}`);
      }
    } catch (err) {
      showToast.error(err?.message || "Unable to enable browser push.");
    } finally {
      window.clearTimeout(busyTimer);
      setBusy(false);
    }
  }

  function dismiss() {
    window.sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="push-reg-banner" role="status" aria-live="polite">
      <div className="push-reg-banner__text">
        <strong>Turn on browser notifications</strong>
        <span>{statusLabel}</span>
        {deliveryHint ? <span className="push-reg-banner__hint">{deliveryHint}</span> : null}
      </div>
      <div className="push-reg-banner__actions">
        <button type="button" className="push-reg-banner__btn" onClick={enablePush} disabled={busy}>
          {busy ? "Enabling…" : "Enable notifications"}
        </button>
        <button type="button" className="push-reg-banner__dismiss" onClick={dismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
